import { DownloadPdfButton } from 'app/src/features/News/components/DownloadPdfButton'
import { newsApi } from 'app/src/services/welfareportal'
import { BreadCrumbClickableItemTemplate, BreadCrumbCurrentItemTemplate, PageWidget } from 'app/src/shared/components'
import { formatDate } from 'app/src/shared/helpers'
import { usePageTitle, useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { DataView } from 'primereact/dataview'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

function NewsListItemTemplate(props: { newsItem: newsApi.News; navigateToNewsDetail: (newsId: string) => void }) {
  const { newsItem, navigateToNewsDetail } = props
  const { t } = useTranslation('news')

  usePageTitle(t('News.Title'))

  const title = (
    <div>
      <div>
        <p className='mb-4 text-base text-black underline decoration-[var(--primary-color)] decoration-4 underline-offset-8'>
          {formatDate(newsItem.date)}
        </p>
      </div>
      <div className='line-clamp-1'>{newsItem.title}</div>
    </div>
  )

  return (
    <div className='grid grid-rows-1'>
      <div className='my-10 grid grid-cols-12'>
        <Card title={title} pt={{ title: { className: 'text-left' } }} className='col-span-12 md:col-span-10 md:col-start-2'>
          <div className='line-clamp-5 max-h-36' dangerouslySetInnerHTML={{ __html: newsItem.content }}></div>
          <div className='mt-4'>
            {newsItem.pdfLink && (
              <div className='flex justify-start'>
                <DownloadPdfButton newsItem={newsItem} />
              </div>
            )}
            <div className='flex justify-end'>
              <Button
                aria-label={t('News.OpenDetail')}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                severity={'outlined' as any}
                rounded
                icon='pi pi-chevron-right'
                onClick={() => navigateToNewsDetail(newsItem.id)}
              ></Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export function NewsList() {
  const context = useWebAppContext()
  const navigate = useWelfarePortalNavigate()
  const { t } = useTranslation('news')
  const [news, setNews] = useState<newsApi.NewsPaginated>()

  const [pagingOffset, setPagingOffset] = useState<number>(0)
  const newsResult = newsApi.useNewsRequestList({
    queryParams: { pagingLimit: 5, pagingOffset },
    pathParams: { templateBasePath: context.userInfo.welfareCampaign.configuration.frontend.templateId },
  })

  useEffect(() => {
    if (newsResult.data && !newsResult.isError) {
      const items = news?.items ?? []
      // eslint-disable-next-line no-unsafe-optional-chaining, @typescript-eslint/no-non-null-asserted-optional-chain
      items.push(...newsResult.data?.items!)
      setNews({
        items,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        paging: newsResult.data?.paging!,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsResult.data, newsResult.isError])

  const loadMore = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    setPagingOffset(news?.items?.length!)
  }, [news])

  useEffect(() => {
    context.update({
      breadCrumbItems: [
        {
          template: () => BreadCrumbClickableItemTemplate({ label: t('News.BackHome'), command: () => navigate.goToLanding() }),
        },
        {
          template: () => BreadCrumbCurrentItemTemplate({ label: t('News.Title') }),
        },
      ],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
  const footer = news?.items?.length! < news?.paging?.total! && (
    <Button className='mx-auto' text icon='pi pi-plus' label={t('News.LoadMore')} onClick={loadMore} />
  )

  return (
    <PageWidget background='cardBackGround'>
      <h1 className='text-3xl font-bold'>{t('News.Title')}</h1>
      <DataView
        pt={{
          content: {
            className: 'bg-inherit',
          },
          footer: {
            className: 'bg-inherit flex',
          },
        }}
        value={news?.items}
        itemTemplate={(newsItem) => {
          return NewsListItemTemplate({
            newsItem,
            navigateToNewsDetail: navigate.goToNewsDetail,
          })
        }}
        footer={footer}
      />
    </PageWidget>
  )
}
